import { useQuery } from "@tanstack/react-query";
import api from "../services/newApi/api";

// Contract of record: C:/workstation/shared-context/api/carlao-analytics-access.ts
// (backend-owned). `CarlaoAnalyticsController` extends `BaseController`, whose
// `.ok()` always envelopes the payload as `{ status, message, data }` — the
// value this hook needs is `response.data.data.allowed`, never
// `response.data.allowed`. Reading from the wrong level yields `undefined`
// (falsy), which would hide the sidebar entry even from an allowed user, and
// FR-010 already requires the entry hidden while loading — so that defect is
// indistinguishable from correct behaviour by eye.
interface CarlaoAnalyticsAccessData {
  allowed: boolean;
}

interface CarlaoAnalyticsAccessResponse {
  status: string;
  message: string;
  data: CarlaoAnalyticsAccessData;
}

/**
 * @param enabled Pass `false` for a caller who can never be answered `true`
 * (e.g. any role other than the Carlão panel's) to skip the request
 * entirely, rather than reading the eventual `allowed: false` as a denial.
 */
export const useCarlaoAccess = (enabled = true) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["carlao-analytics-access"],
    queryFn: async () => {
      const response = await api.get<CarlaoAnalyticsAccessResponse>(
        "/internal/carlao/analytics/access",
      );
      return response.data.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    allowed: isLoading ? false : (data?.allowed ?? false),
    loading: isLoading,
    // True only once the request has settled into an error after its
    // retries are exhausted — a failed check, not a denial. `allowed` is
    // `false` in both cases, so a consumer that must tell them apart (e.g.
    // to show a retry affordance instead of "you're not on the list") needs
    // this flag; a consumer that only cares about "may I show the gated UI"
    // (e.g. the sidebar) can keep failing closed on `allowed` alone.
    error: isError,
    refetch,
  };
};
